import * as React from "react"
import { jsonFetch } from "../bookingApi"
import { POSTCODE_VALIDATION_MESSAGE } from "../dateSelection"
import type { Availability } from "../types"
import type { useBookingSelection } from "./useBookingSelection"
import type { useCheckoutAnalytics } from "./useCheckoutAnalytics"
import type { useCheckoutLifecycle } from "./useCheckoutLifecycle"
import type { useCheckoutModel } from "./useCheckoutModel"
import type { useCheckoutState } from "./useCheckoutState"

type Inputs = Pick<ReturnType<typeof useCheckoutState>,
    "product" | "activeSlug" | "availabilityController" | "setError" | "setPhase" | "setAvailability" | "quantity" | "availability" | "draftRestored" | "journeyStep" | "phase" | "postcode" | "settledPostcode" | "bookingPane">
    & Pick<ReturnType<typeof useCheckoutModel>,
        "selection" | "baseUrl" | "choice" | "deliveryContactRequired">
    & Pick<ReturnType<typeof useCheckoutLifecycle>,
        "cancelAvailabilityCheck">
    & Pick<ReturnType<typeof useBookingSelection>,
        "commonRequest">
    & Pick<ReturnType<typeof useCheckoutAnalytics>,
        "analytics">

export function useBookingAvailability(inputs: Inputs) {
    const {
        product,
        deliveryContactRequired,
        activeSlug,
        selection,
        cancelAvailabilityCheck,
        availabilityController,
        setError,
        setPhase,
        baseUrl,
        commonRequest,
        setAvailability,
        analytics,
        choice,
        quantity,
        availability,
        draftRestored,
        journeyStep,
        phase,
        postcode,
        settledPostcode,
        bookingPane,
    } = inputs
    const checkAvailability = React.useCallback(async () => {
        if (deliveryContactRequired || !product || product.slug !== activeSlug || !selection) return null
        // Replace any pending automatic check for the previous inputs.
        cancelAvailabilityCheck()
        const controller = new AbortController()
        availabilityController.current = controller
        setError("")
        setPhase("checking")
        try {
            const result = await jsonFetch<Availability>(`${baseUrl}/v1/availability`, {
                method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(commonRequest), signal: controller.signal,
            })
            // A response belongs only to the inputs that started its request.
            if (controller.signal.aborted) return null
            setAvailability(result)
            setPhase("ready")
            analytics("hire_availability_checked", { rate: choice?.rateLabel, quantity, availability_result: result.available ? "available" : result.reasonCode })
            return result
        } catch (reason) {
            if (controller.signal.aborted) return null
            setAvailability(null)
            setError((reason as { code?: string })?.code === "INVALID_POSTCODE"
                ? POSTCODE_VALIDATION_MESSAGE
                : reason instanceof Error ? reason.message : "Availability could not be checked.")
            setPhase("ready")
            return null
        } finally {
            if (availabilityController.current === controller) availabilityController.current = null
        }
    }, [deliveryContactRequired, activeSlug, analytics, baseUrl, cancelAvailabilityCheck, choice?.rateLabel, commonRequest, product, quantity, selection])

    // Restoring an add-ons draft has no availability snapshot yet. Fetch the
    // delivery fee there too, so refresh/history cannot leave it out of the total.
    const needsAvailabilityCheck = !availability
    // Restoring a paid booking must not restart selection checks or replace confirmation.
    const canCheckAvailability = !deliveryContactRequired && draftRestored && journeyStep === "booking" && (phase === "ready" || phase === "checking")
    React.useEffect(() => {
        if (canCheckAvailability && needsAvailabilityCheck && selection && postcode !== "" && postcode === settledPostcode) {
            void checkAvailability()
        }
        return cancelAvailabilityCheck
    }, [bookingPane, canCheckAvailability, needsAvailabilityCheck, cancelAvailabilityCheck, checkAvailability, postcode, settledPostcode, quantity, selection])
}
